import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { autorun, observable } from "mobx";
import moment from "moment";
import { Button, Icon, Confirm, Form, Grid, Header, Message, Label } from "semantic-ui-react";
import { Link } from "react-router-dom";
import TourLayoutWrapper from "../components/TourLayoutWrapper";
import TourNav from "../constants/TourNav";
import TourStore from "~/stores/TourStore";
import Loader from "../components/Loader";
import ModalError from "~/components/element/ModalError";
import VideoRequestStatus from "~/constants/Tour/VideoRequestStatus";
import YouTubeStore from "~/stores/YouTubeStore";
import TourExportedTourVideoStore from "~/stores/Tour/TourExportedTourVideoStore";
import JobStatus from "../components/JobStatus";
import ContentLoader from "~/components/element/ContentLoader";
import TourVideoRequestStore from "~/stores/Tour/TourVideoRequestStore";
import TourVideoRegenerationStore from "~/stores/Tour/TourVideoRegenerationStore";
import TourYouTubeVideoStore from "~/stores/Tour/TourYouTubeVideoStore";
import "../styles/VideoRequest.less";
import FormRadioGroup from "../../../components/element/FormRadioGroup";

@observer
class VideoRequest extends Component {
  /**
   * @type {YouTubeStore}
   */
  @observable youTubeStore = null;

  @observable tourExportedTourVideoStore = null;

  tourVideoRequestStore = null;

  tourVideoRegenerationStore = null;

  tourYouTubeVideoStore = null;

  constructor(props) {
    super(props);
    document.title = "Tour - Video Request";

    this.state = {
      requestVideoModal: false,
      requestVideoLoading: false,
      requestVideoDeleteModal: false,
      confirmUploadVideoModal: false,
      regenerateVideoLoading: false,
    };
    const { i18n } = props;
    this.youTubeStore = new YouTubeStore();
    this.tourExportedTourVideoStore = new TourExportedTourVideoStore();
    this.tourExportedTourVideoStore.locale = i18n.language;
    this.tourVideoRequestStore = new TourVideoRequestStore();
    this.tourVideoRegenerationStore = new TourVideoRegenerationStore();
    this.tourYouTubeVideoStore = new TourYouTubeVideoStore();
  }

  componentDidMount() {
    const { tourId } = this.props;
    this.tourExportedTourVideoStore.load(tourId);
    window.scrollTo(0, 0);
    // wait for exportedTourVideoStore to get loaded if required and check for any existing job if status is upload
    // pending.
    autorun(reaction => {
      const { tourExportedTourVideo } = this.tourExportedTourVideoStore;

      if (!tourExportedTourVideo.tourId) {
        return;
      }

      if (tourExportedTourVideo.requestStatus === VideoRequestStatus.REQUEST_STATUS_YOUTUBE_UPLOAD_PENDING) {
        if (this.youTubeStore.uploadJob) {
          reaction.dispose();

          return;
        }

        this.youTubeStore.getUploadJobId(tourId).then(() => {
          if (this.youTubeStore.uploadJob) {
            this.youTubeStore.startPulling();
          }
        });
      }

      reaction.dispose();
    });
  }

  componentWillUnmount() {
    this.youTubeStore.stopPulling();
  }

  requestVideo = () => {
    const { tourId } = this.props;

    this.setState({ requestVideoModal: false, requestVideoLoading: true });
    this.tourVideoRequestStore.create(tourId).then(() => {
      this.setState({ requestVideoLoading: false });
      this.tourExportedTourVideoStore.load(tourId);
    });
  };

  regenerateVideo = () => {
    const { tourId } = this.props;
    this.setState({ regenerateVideoLoading: true });
    this.tourVideoRegenerationStore.create(tourId).then(() => {
      this.setState({ regenerateVideoLoading: false });
      this.tourExportedTourVideoStore.load(tourId);
    });
  };

  deleteVideo = () => {
    const { tourId } = this.props;
    this.setState({ requestVideoDeleteModal: true });
    this.tourYouTubeVideoStore
      .delete(tourId)
      .then(() => {
        this.setState({ requestVideoDeleteModal: false });
      })
      .catch(() => this.setState({ requestVideoDeleteModal: false }))
      .finally(() => {
        this.youTubeStore.uploadJob = null;
        this.tourExportedTourVideoStore.load(tourId);
      });
  };

  uploadVideoToYoutube = () => {
    const { tourId } = this.props;
    this.setState({ confirmUploadVideoModal: false });
    this.youTubeStore.uploadVideo(tourId);
  };

  updateYoutubeSettings = () => {
    const { tourId } = this.props;
    this.tourExportedTourVideoStore.updateYoutubeSettings(tourId).then(() => {
      this.youTubeStore.uploadVideo(tourId);
    });
  };

  render() {
    const { tourStore, tourId, t } = this.props;
    const { isLoading, tourExportedTourVideo } = this.tourExportedTourVideoStore;

    if (isLoading) {
      return <ContentLoader />;
    }

    const {
      requestVideoModal,
      requestVideoLoading,
      requestVideoDeleteModal,
      regenerateVideoLoading,
      confirmUploadVideoModal,
    } = this.state;

    let noRequestContent;
    let requestSentContent;
    let regenerateVideoContent;
    let videoRenderingContent;
    let youtubeConnectContent;
    let youtubeUploadContent;
    let youtubeViewContent;

    if (
      tourExportedTourVideo.requestStatus === VideoRequestStatus.REQUEST_STATUS_NEVER_GENERATED &&
      tourExportedTourVideo.canRequestVideo &&
      !tourExportedTourVideo.requestTime
    ) {
      noRequestContent = (
        <>
          <p>{t("clientPanelVideo:thereIsNoVideoExportedForThisTour")}</p>
          <p>{tourExportedTourVideo.exportToVideoSpecialInstructions}</p>
          <Button icon primary onClick={() => this.setState({ requestVideoModal: true })}>
            <Icon name="video" /> {t("clientPanelVideo:requestVideo")}
          </Button>
          <Confirm
            open={requestVideoModal}
            content={t("clientPanelVideo:areYouSureRequestVideo")}
            confirmButton={t("confirm")}
            onCancel={() => this.setState({ requestVideoModal: false })}
            onConfirm={this.requestVideo}
          />
        </>
      );
    }

    if (
      tourExportedTourVideo.requestStatus === VideoRequestStatus.REQUEST_STATUS_NEVER_GENERATED &&
      tourExportedTourVideo.requestTime
    ) {
      const time = moment(tourExportedTourVideo.requestTime);
      requestSentContent = <p>{t("clientPanelVideo:videoForTourRequestedTime", { timeFromNow: time.fromNow() })}</p>;
    }

    if (
      tourExportedTourVideo.requestStatus === VideoRequestStatus.REQUEST_STATUS_VIDEO_OUT_OF_DATE ||
      tourExportedTourVideo.requestStatus === VideoRequestStatus.REQUEST_STATUS_ARCHIVED
    ) {
      regenerateVideoContent = (
        <>
          <p>
            {tourExportedTourVideo.requestStatus === VideoRequestStatus.REQUEST_STATUS_VIDEO_OUT_OF_DATE
              ? t("clientPanelVideo:tourHasChangedSinceVideoExported")
              : t("clientPanelVideo:tourVideoHasBeenArchived")}
          </p>
          <Button icon primary onClick={this.regenerateVideo}>
            <Icon name="video" /> {t("clientPanelVideo:regenerateVideo")}
          </Button>
        </>
      );
    }

    if (tourExportedTourVideo.requestStatus === VideoRequestStatus.REQUEST_STATUS_RUNNING && tourStore.tour.tourId) {
      videoRenderingContent = <p>{t("clientPanelVideo:videoRenderingComeBackSoon")}</p>;
    }

    if (
      tourExportedTourVideo.requestStatus === VideoRequestStatus.REQUEST_STATUS_YOUTUBE_NOT_CONNECTED &&
      tourStore.tour.tourId
    ) {
      youtubeConnectContent = (
        <>
          <h3>{t("youtube:youtube")}</h3>
          <p>{t("youtube:accountNotSetUpAutomatedPublishing")}</p>
          <Form method="GET" action={tourExportedTourVideo.youTube.connectUrl} target="_blank">
            <Button type="submit" color="youtube">
              <Icon name="youtube" /> {t("youtube:setUpYoutubePublishing")}
            </Button>
          </Form>
        </>
      );
    }

    if (
      tourStore.tour.tourId &&
      (tourExportedTourVideo.requestStatus === VideoRequestStatus.REQUEST_STATUS_YOUTUBE_UPLOAD_PENDING ||
        tourExportedTourVideo.youTube.sendToYouTubeUrl)
    ) {
      youtubeUploadContent = (
        <>
          <h3>{t("youtube:youtube")}</h3>
          <Grid>
            {this.youTubeStore.uploadJob && (
              <>
                <Grid.Row>
                  <Grid.Column>
                    <JobStatus currentStatusOnly={false} job={this.youTubeStore.uploadJob} />
                  </Grid.Column>
                </Grid.Row>
              </>
            )}
            <Grid.Row>
              <Grid.Column>
                <Button
                  type="submit"
                  color="youtube"
                  disabled={this.youTubeStore.uploadJob && this.youTubeStore.uploadJob.isRunning}
                  onClick={() => this.setState({ confirmUploadVideoModal: true })}
                >
                  <Icon name="youtube" /> {t("clientPanelVideo:sendTourVideoToYoutube")}
                </Button>
                <Confirm
                  open={confirmUploadVideoModal}
                  content={t("clientPanelVideo:areYouSureUploadVideo")}
                  onConfirm={this.uploadVideoToYoutube}
                  onCancel={() => this.setState({ confirmUploadVideoModal: false })}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </>
      );
    }

    if (
      tourStore.tour.tourId &&
      (tourExportedTourVideo.requestStatus === VideoRequestStatus.REQUEST_STATUS_YOUTUBE_UPLOADED ||
        tourExportedTourVideo.youTube.viewUrl)
    ) {
      youtubeViewContent = (
        <>
          <h3>{t("youtube:youtube")}</h3>
          <Grid>
            {this.youTubeStore.uploadJob && (
              <>
                <Grid.Row>
                  <Grid.Column>
                    <JobStatus currentStatusOnly={false} job={this.youTubeStore.uploadJob} />
                  </Grid.Column>
                </Grid.Row>
              </>
            )}
            <Grid.Row>
              <Grid.Column>
                <Form>
                  <Form.Group widths="equal">
                    <Form.Field>
                      <Label>Title</Label>
                      <Form.TextArea
                        onChange={(e, data) => this.tourExportedTourVideoStore.setYoutubeValue("title", data.value)}
                        value={tourExportedTourVideo.youTube.title}
                      />
                    </Form.Field>
                    <Form.Field>
                      <Label>Description</Label>
                      <Form.TextArea
                        onChange={(e, data) =>
                          this.tourExportedTourVideoStore.setYoutubeValue("description", data.value)
                        }
                        value={tourExportedTourVideo.youTube.description}
                      />
                    </Form.Field>
                    <Form.Field>
                      <FormRadioGroup
                        onChange={(property, value) => this.tourExportedTourVideoStore.setYoutubeValue(property, value)}
                        title="Privacy Status"
                        name="privacyStatus"
                        options={[
                          {
                            id: "public",
                            value: "public",
                            label: "Public",
                            checked: tourExportedTourVideo.youTube.privacyStatus === "public",
                          },
                          {
                            id: "private",
                            value: "private",
                            label: "Private",
                            checked: tourExportedTourVideo.youTube.privacyStatus === "private",
                          },
                          {
                            id: "unlisted",
                            value: "unlisted",
                            label: "Unlisted",
                            checked: tourExportedTourVideo.youTube.privacyStatus === "unlisted",
                          },
                        ]}
                      />
                    </Form.Field>
                  </Form.Group>
                </Form>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <Button onClick={() => this.updateYoutubeSettings()} primary content="Update Youtube Settings" />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={2}>
              <Grid.Column floated="left">
                <Form method="GET" action={tourExportedTourVideo.youTube.viewUrl} target="_blank">
                  <Button type="submit" color="youtube">
                    <Icon name="youtube" /> {t("clientPanelVideo:viewVideo")}
                  </Button>
                </Form>
              </Grid.Column>
              <Grid.Column floated="right" textAlign="right">
                {tourExportedTourVideo.youTube.canDeleteVideo && (
                  <>
                    <Button icon onClick={() => this.setState({ requestVideoDeleteModal: true })}>
                      <Icon name="delete" /> {t("youtube:removeVideoFromYoutube")}
                    </Button>
                    <Confirm
                      open={requestVideoDeleteModal}
                      content={t("youtube:areYouSureRemoveVideoFromYoutube")}
                      confirmButton={t("delete")}
                      onCancel={() => this.setState({ requestVideoDeleteModal: false })}
                      onConfirm={this.deleteVideo}
                    />
                  </>
                )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </>
      );
    }

    return (
      <>
        <Loader active={isLoading} page content={t("loadingDots")} />
        <Loader active={requestVideoLoading || regenerateVideoLoading} page content={t("sendingRequest")} />
        <ModalError nestStore={tourStore} />
        <ModalError nestStore={this.youTubeStore} />
        <ModalError nestStore={this.tourExportedTourVideoStore} />
        <ModalError nestStore={this.tourVideoRequestStore} />
        <ModalError nestStore={this.tourVideoRegenerationStore} />
        <ModalError nestStore={this.tourYouTubeVideoStore} />
        <Header as="h2">
          <Header.Content>
            {t("clientPanelVideo:videoRequest")}
            <Header.Subheader>
              <Link to={`/tours/${tourId}/download#video-download`}>{t("clientPanelVideo:downloadVideos")}</Link>
            </Header.Subheader>
          </Header.Content>
        </Header>
        {this.tourExportedTourVideoStore.youtubeUpdateStatus === false && (
          <Message error content={this.tourExportedTourVideoStore.youtubeErrorMessage} />
        )}
        {this.tourExportedTourVideoStore.youtubeUpdateStatus === true && (
          <Message success content="Successfully saved youtube settings" />
        )}
        <Message className="video-request-message-container">
          <Message.List>
            <Message.Item>
              {t("clientPanelVideo:videoDownloadsMoveTo")}{" "}
              <Link to={`/tours/${tourId}/download#video-download`}>
                <u>{t("clientPanelMisc:downloadCenter")}</u>
              </Link>
              . {t("clientPanelVideo:downloadLinksAppearUnderPageTitle")}
            </Message.Item>
            <Message.Item>{t("clientPanelVideo:noteGeneratingHDVideoTakeHours")}</Message.Item>
          </Message.List>
        </Message>
        <div className="video-request-description">{t("clientPanelVideo:videoVersionTourSingleMovieFile")}</div>
        {noRequestContent}
        {requestSentContent}
        {regenerateVideoContent}
        {videoRenderingContent}

        {youtubeConnectContent}
        {youtubeUploadContent}
        {youtubeViewContent}
      </>
    );
  }
}

VideoRequest.propTypes = {
  tourStore: PropTypes.instanceOf(TourStore),
  tourId: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.object.isRequired,
};

export default withTranslation()(TourLayoutWrapper(VideoRequest, TourNav.VIDEO));
